/* RevenueIntelligenceTab — summary KPIs, category & channel splits,
 * hourly trend chart. Fully live for every outlet. */
import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const PCT = (n) => `${n > 0 ? '+' : ''}${Number(n || 0).toFixed(1)}%`;

export default function RevenueIntelligenceTab({ outletId, outlet }) {
  const [summary, setSummary] = useState(null);
  const [byCategory, setByCategory] = useState(null);
  const [byChannel, setByChannel] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, c, ch, h] = await Promise.all([
          api.get(`/outlet-intel/${outletId}/revenue/summary`),
          api.get(`/outlet-intel/${outletId}/revenue/by-category?days=7`),
          api.get(`/outlet-intel/${outletId}/revenue/by-channel?days=7`),
          api.get(`/outlet-intel/${outletId}/revenue/hourly-trend?days=7`),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setByCategory(c.data);
        setByChannel(ch.data);
        setHourly(h.data);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [outletId]);

  if (loading) return <div className="text-sm" style={{ color: '#6B7280' }}>Loading revenue intelligence…</div>;
  if (!summary) return <div className="text-sm" style={{ color: '#DC2626' }}>Failed to load.</div>;

  const t = summary.today;
  const y = summary.yesterday;
  const delta = summary.today_vs_yesterday_pct;
  const isUp = delta >= 0;

  return (
    <div className="space-y-4" data-testid="revenue-intel">
      {/* KPI Row — today */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Today Revenue" value={INR(t.revenue)} sub={`${t.orders} orders · avg ${INR(t.avg_check)}`} highlight testid="kpi-today-rev" />
        <KpiCard label="Yesterday" value={INR(y.revenue)} sub={`${y.orders} orders`} testid="kpi-yesterday-rev" />
        <KpiCard
          label="vs Yesterday"
          value={PCT(delta)}
          sub={isUp ? 'trending up' : 'trending down'}
          icon={isUp ? TrendingUp : TrendingDown}
          iconColor={isUp ? '#10B981' : '#DC2626'}
          testid="kpi-delta"
        />
        <KpiCard label="WoW (7d)" value={INR(summary.wow_7d.revenue)} sub={`${summary.wow_7d.orders} orders`} testid="kpi-wow" />
        <KpiCard label="MoM (30d)" value={INR(summary.mom_30d.revenue)} sub={`margin ${summary.mom_30d.margin_pct}%`} testid="kpi-mom" />
      </div>

      {/* Category + Channel split */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Revenue by Category (7d)" testid="card-category">
          {byCategory?.rows?.length ? (
            byCategory.rows.map(r => (
              <SplitRow key={r.category} label={r.category}
                amount={INR(r.revenue)} share={r.share_pct}
                sub={`${r.qty} items sold`} />
            ))
          ) : <Empty />}
          <FooterTotal label="Total" amount={INR(byCategory?.total)} />
        </Card>

        <Card title="Revenue by Channel (7d)" testid="card-channel">
          {byChannel?.rows?.length ? (
            byChannel.rows.map(r => (
              <SplitRow key={r.channel} label={formatChannel(r.channel)}
                amount={INR(r.revenue)} share={r.share_pct}
                sub={`${r.orders} orders`} />
            ))
          ) : <Empty />}
          <FooterTotal label="Total" amount={INR(byChannel?.total)} />
        </Card>
      </div>

      {/* Hourly trend — simple ASCII bars */}
      <Card title="Hourly Revenue Trend (avg of last 7 days)" testid="card-hourly">
        <HourlyBarChart rows={hourly?.rows || []} />
      </Card>

      {/* Signals */}
      <Card title="Signals & Observations" testid="card-signals">
        <Signals summary={summary} byCategory={byCategory} byChannel={byChannel} />
      </Card>
    </div>
  );
}

function formatChannel(c) {
  return ({
    dine_in: 'Dine-in',
    takeaway: 'Takeaway',
    zomato: 'Zomato',
    swiggy: 'Swiggy',
    room_service: 'Room Service',
  }[c] || c);
}

function KpiCard({ label, value, sub, highlight, icon: Icon, iconColor, testid }) {
  return (
    <div className="rounded-xl p-4" style={{
      backgroundColor: highlight ? '#1B2A4A' : 'white',
      color: highlight ? 'white' : '#1B2A4A',
      border: highlight ? 'none' : '1px solid #E5E1DC',
    }} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider mb-1"
        style={{ color: highlight ? '#9DB5CC' : '#6B7280' }}>{label}</p>
      <div className="flex items-baseline gap-2">
        <p className="text-2xl font-semibold">{value}</p>
        {Icon && <Icon size={16} color={iconColor} />}
      </div>
      <p className="text-[11px] mt-1" style={{ color: highlight ? '#9DB5CC' : '#6B7280' }}>{sub}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl bg-white p-5" style={{ border: '1px solid #E5E1DC' }} data-testid={testid}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>{title}</h3>
      {children}
    </div>
  );
}

function SplitRow({ label, amount, share, sub }) {
  return (
    <div className="py-2 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-baseline mb-1">
          <span className="text-[13px] font-medium" style={{ color: '#1B2A4A' }}>{label}</span>
          <span className="text-[13px] font-semibold">{amount}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
            <div className="h-full rounded-full" style={{
              width: `${Math.min(share, 100)}%`, backgroundColor: '#2A7B88'
            }} />
          </div>
          <span className="text-[10px] w-10 text-right" style={{ color: '#6B7280' }}>{share}%</span>
        </div>
        <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>{sub}</p>
      </div>
    </div>
  );
}

function FooterTotal({ label, amount }) {
  return (
    <div className="mt-3 pt-2 flex justify-between text-[12px] font-semibold" style={{ borderTop: '1px solid #E5E1DC' }}>
      <span style={{ color: '#6B7280' }}>{label}</span>
      <span style={{ color: '#1B2A4A' }}>{amount}</span>
    </div>
  );
}

function Empty() {
  return <p className="text-[11px] py-3" style={{ color: '#9CA3AF' }}>No data for this window.</p>;
}

function HourlyBarChart({ rows }) {
  const max = Math.max(...rows.map(r => r.revenue_avg), 1);
  return (
    <div className="space-y-0.5">
      {rows.map(r => (
        <div key={r.hour} className="flex items-center gap-2 text-[10px]">
          <span className="w-10 font-mono" style={{ color: '#6B7280' }}>{r.label}</span>
          <div className="flex-1 h-4 rounded-sm overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
            <div className="h-full rounded-sm flex items-center justify-end pr-1" style={{
              width: `${(r.revenue_avg / max) * 100}%`,
              backgroundColor: r.revenue_avg > max * 0.6 ? '#F59E0B' : '#2A7B88',
            }}>
              {r.revenue_avg > max * 0.25 && (
                <span className="text-[9px] text-white font-semibold">₹{Math.round(r.revenue_avg/1000)}k</span>
              )}
            </div>
          </div>
          <span className="w-14 text-right" style={{ color: '#6B7280' }}>{r.orders_avg} ord</span>
        </div>
      ))}
    </div>
  );
}

function Signals({ summary, byCategory, byChannel }) {
  const signals = [];
  const delta = summary.today_vs_yesterday_pct;
  if (delta < -15) signals.push({ tone: 'warn', text: `Today is tracking ${PCT(delta)} vs yesterday — worth checking with the team.` });
  if (delta > 20) signals.push({ tone: 'good', text: `Today is up ${PCT(delta)} vs yesterday — celebrate it.` });
  const liquorRow = byCategory?.rows?.find(r => r.category === 'Liquor');
  if (liquorRow?.share_pct < 8 && byCategory?.total > 100000) {
    signals.push({ tone: 'info', text: `Liquor share only ${liquorRow.share_pct}% — bar upsell opportunity.` });
  }
  const zomatoRow = byChannel?.rows?.find(r => r.channel === 'zomato');
  const swiggyRow = byChannel?.rows?.find(r => r.channel === 'swiggy');
  const aggr = (zomatoRow?.share_pct || 0) + (swiggyRow?.share_pct || 0);
  if (aggr > 30) signals.push({ tone: 'info', text: `Aggregators (Zomato+Swiggy) at ${aggr.toFixed(0)}% of revenue — commission drag worth watching.` });
  if (summary.today.margin_pct < 55) signals.push({ tone: 'warn', text: `Today's margin ${summary.today.margin_pct}% is below 55% — check discount codes or cost creep.` });

  if (!signals.length) return <p className="text-[12px]" style={{ color: '#6B7280' }}>No flags — operations look steady.</p>;

  return (
    <div className="space-y-2">
      {signals.map((s, i) => (
        <div key={i} className="flex items-start gap-2 text-[12px] p-2.5 rounded-lg" style={{
          backgroundColor: s.tone === 'warn' ? '#FEF3C7' : s.tone === 'good' ? '#ECFDF5' : '#EFF6FF',
          color: s.tone === 'warn' ? '#92400E' : s.tone === 'good' ? '#065F46' : '#1E40AF',
        }}>
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{s.text}</span>
        </div>
      ))}
    </div>
  );
}
